import { useEffect, useState } from "react";
import { Card, Table, Tag, Input, Spin, Layout, Segmented, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { postAPI } from "../../utils/common";
import { UPCOMING_BILLING } from "../../constants/api";
import { BILLING_CYCLE, HOTEL_ID_SERIES, CURRENCY } from "../../constants/constent";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import VendorBillingDetailModal from "./blocs/VendorBillingDetailModal";

function UpcomingBillingDates() {
  const { Content } = Layout;

  const [state, setState] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [period, setPeriod] = useState("all");

  // Detail modal
  const [modalOpen, setModalOpen] = useState(false);
  const [detailVendorId, setDetailVendorId] = useState(null);

  /* ================= FETCH ================= */

  const getList = (selectedPeriod = period) => {
    setLoading(true);
    postAPI(
      UPCOMING_BILLING,
      { period: selectedPeriod },
      (res) => {
        if (res.status) {
          const rows = res.data.map((row, index) => ({
            key: row.id,
            serial: index + 1,
            ...row,
          }));
          setState(rows);
          applySearch(rows, searchText);
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };

  useEffect(() => {
    getList(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  /* ================= SEARCH ================= */

  const applySearch = (rows, value) => {
    const search = (value || "").toLowerCase().trim();
    if (!search) {
      setFilteredData(rows);
      return;
    }
    setFilteredData(
      rows.filter((item) =>
        Object.values(item).some(
          (val) =>
            typeof val === "string" && val.toLowerCase().includes(search),
        ),
      ),
    );
  };

  const handleSearch = (value) => {
    setSearchText(value);
    applySearch(state, value);
  };

  /* ================= DETAIL ================= */

  const openDetail = (vendorId) => {
    setDetailVendorId(vendorId);
    setModalOpen(true);
  };

  /* ================= HELPERS ================= */

  const getBillingCycleLabel = (value) => {
    const match = BILLING_CYCLE.find((c) => c.value === Number(value));
    return match ? match.label : "-";
  };

  const money = (v) =>
    v === null || v === undefined || v === ""
      ? "-"
      : `${CURRENCY}${Number(v).toLocaleString("en-IN")}`;

  const fmtDate = (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "-");

  const renderVendorStatus = (value) => {
    if (value === "active") return <Tag color="green">Active</Tag>;
    if (value === "inactive") return <Tag color="red">Inactive</Tag>;
    if (value === "pending") return <Tag color="orange">Pending</Tag>;
    return <Tag>NA</Tag>;
  };

  /* ================= TABLE ================= */

  const columns = [
    { title: "S. No.", dataIndex: "serial", width: 70 },
    {
      title: "Hotel ID",
      render: (_, row) => HOTEL_ID_SERIES + row.hotel_id,
    },
    { title: "Hotel Name", dataIndex: "hotel_name" },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => renderVendorStatus(status),
    },
    {
      title: "Next Billing Date",
      dataIndex: "next_billing_date",
      render: (date) => fmtDate(date),
    },
    {
      title: "Billing Cycle",
      dataIndex: "billing_cycle",
      render: (value) => getBillingCycleLabel(value),
    },
    {
      title: "Last Billed On",
      dataIndex: "last_billed_on",
      render: (date) => fmtDate(date),
    },
    {
      title: "Outstanding",
      dataIndex: "total_outstanding",
      render: (v) => money(v),
    },
    {
      title: "Auto-Gen",
      dataIndex: "auto_bill_generation",
      render: (value) =>
        value ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
    },
    {
      title: "Action",
      fixed: "right",
      width: 80,
      align: "center",
      render: (_, row) => (
        <Tooltip title="Billing & Finance Details">
          <EyeOutlined
            onClick={() => openDetail(row.id)}
            style={{
              fontSize: 18,
              color: "var(--primary-color)",
              cursor: "pointer",
            }}
          />
        </Tooltip>
      ),
    },
  ];

  /* ================= RETURN ================= */

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Spin spinning={loading}>
          <Card
            title="Upcoming Billing Dates"
            extra={
              <Input
                placeholder="Search vendors..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 240 }}
              />
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Segmented
                value={period}
                onChange={(val) => setPeriod(val)}
                options={[
                  { label: "This Week", value: "week" },
                  { label: "This Month", value: "month" },
                  { label: "This Year", value: "year" },
                  { label: "All", value: "all" },
                ]}
              />
            </div>
            <Table
              rowKey="key"
              loading={loading}
              columns={columns}
              dataSource={filteredData}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </Spin>
      </Content>
      <LayoutFooter />

      <VendorBillingDetailModal
        open={modalOpen}
        vendorId={detailVendorId}
        onClose={() => setModalOpen(false)}
      />
    </Layout>
  );
}

export default UpcomingBillingDates;
